export type Balance = {
  amount: number | null;
  status: "loading" | "ok" | "missing_key" | "error";
};
export type DashboardBalances = Record<"grizzly" | "onlinesim", Balance>;

async function getBalance(
  provider: keyof DashboardBalances,
  key: unknown,
  signal: AbortSignal
): Promise<Balance> {
  if (typeof key !== "string" || !key.trim()) {
    return { amount: null, status: "missing_key" };
  }
  try {
    const grizzly = provider === "grizzly";
    const url = new URL(grizzly
      ? "https://api.grizzlysms.com/stubs/handler_api.php"
      : "https://onlinesim.io/api/getBalance.php");
    url.searchParams.set(grizzly ? "api_key" : "apikey", key.trim());
    if (grizzly) url.searchParams.set("action", "getBalance");
    const response = await fetch(url.toString(), {
      signal,
      credentials: "omit",
      cache: "no-store",
      referrerPolicy: "no-referrer",
    });
    if (!response.ok) throw new Error("Balance request failed");
    let value: unknown;
    if (grizzly) {
      const match = (await response.text()).trim().match(/^ACCESS_BALANCE:([0-9]+(?:\.[0-9]+)?)$/);
      if (!match) throw new Error("Invalid provider response");
      value = match[1];
    } else {
      const data = await response.json();
      if (String(data?.response) !== "1") throw new Error("Invalid provider response");
      value = data.balance;
    }
    if ((typeof value !== "number" && typeof value !== "string") ||
        (typeof value === "string" && !value.trim())) throw new Error("Invalid balance");
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount < 0) throw new Error("Invalid balance");
    return { amount, status: "ok" };
  } catch {
    return { amount: null, status: "error" };
  }
}

export async function requestDashboardBalances(signal: AbortSignal): Promise<DashboardBalances> {
  const keys = JSON.parse(process.env.REACT_APP_DASHBOARD_KEYS || "{}");
  const [grizzly, onlinesim] = await Promise.all([
    getBalance("grizzly", keys.grizzly, signal),
    getBalance("onlinesim", keys.onlinesim, signal),
  ]);
  return { grizzly, onlinesim };
}
